// ═══════════════════════════════════════════════════════════════════════════
//  HIASAN LAYAR TV (5 Okt 2026) — latar hidup, kartu judul, "berikutnya",
//  equalizer, confetti, angka skor yang menghitung naik.
//
//  Aturan untuk TV box Android murah:
//    · animasi HANYA transform/opacity (tidak memicu layout/repaint);
//    · tanpa filter blur — gradasi radial sudah lembut dari sananya;
//    · semuanya pointer-events:none, tidak pernah menghalangi pemutar YouTube;
//    · prefers-reduced-motion dihormati (lihat akhir pemutar.css).
// ═══════════════════════════════════════════════════════════════════════════

import { useEffect, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { Mic } from 'lucide-react'
import type { BarisAntrean } from '../../lib/tamu'

/** Latar aurora bergerak halus + partikel ringan. Taruh sebagai anak pertama
 *  pembungkus `relative`. */
export function LatarHidup({ partikel = true, redup = false }: { partikel?: boolean; redup?: boolean }) {
  return (
    <div className={`tv-latar ${redup ? 'tv-latar-redup' : ''}`} aria-hidden>
      <div className="tv-aurora tv-a1" />
      <div className="tv-aurora tv-a2" />
      <div className="tv-aurora tv-a3" />
      <div className="tv-aurora tv-a4" />
      {partikel && (
        <div className="tv-partikel">
          {Array.from({ length: 14 }).map((_, i) => (
            <i key={i} style={{
              left: `${(i * 37 + 11) % 100}%`,
              animationDuration: `${16 + (i % 5) * 4}s`,
              animationDelay: `${-(i * 2.3)}s`,
              width: i % 3 === 0 ? 4 : 2, height: i % 3 === 0 ? 4 : 2,
            }} />
          ))}
        </div>
      )}
      <div className="tv-vignette" />
    </div>
  )
}

/** Kartu judul saat lagu mulai — tampil ±3 dtk lalu hilang sendiri. */
export function KartuMasuk({ judul, artis, penyanyi, sumber, selesai }: {
  judul: string; artis: string; penyanyi?: string | null; sumber?: string
  selesai?: () => void
}) {
  const [tampil, setTampil] = useState(true)
  const selesaiRef = useRef(selesai)
  selesaiRef.current = selesai
  useEffect(() => {
    const t = window.setTimeout(() => { setTampil(false); selesaiRef.current?.() }, 3300)
    return () => window.clearTimeout(t)
  }, [])
  if (!tampil) return null
  return (
    <div className="tv-masuk-wadah" aria-hidden>
      <div className="tv-masuk-kartu">
        <div className="tv-masuk-label">
          <span className="tv-masuk-titik" />
          {sumber === 'youtube' ? 'YouTube' : sumber === 'spotify' ? 'Spotify' : 'Sekarang'}
        </div>
        <div className="tv-masuk-judul">{judul}</div>
        {artis && <div className="tv-masuk-artis">{artis}</div>}
        <div className="tv-masuk-garis" />
        {penyanyi && (
          <div className="tv-masuk-penyanyi">
            <Mic size={18} /> dinyanyikan oleh <b>{penyanyi}</b>
          </div>
        )}
      </div>
    </div>
  )
}

/** "Berikutnya" kecil di pojok kanan atas — muncul di 20 detik terakhir. */
export function BerikutnyaPojok({ baris, tampil }: { baris?: BarisAntrean; tampil: boolean }) {
  if (!baris) return null
  return (
    <div className={`tv-berikut ${tampil ? 'tv-berikut-tampil' : ''}`} aria-hidden>
      {baris.sampul
        ? <img src={baris.sampul} alt="" className="tv-berikut-sampul" />
        : <div className="tv-berikut-sampul tv-berikut-sampul-kosong">♪</div>}
      <div className="min-w-0">
        <div className="tv-berikut-label">Berikutnya</div>
        <div className="tv-berikut-judul">{baris.judul}</div>
        <div className="tv-berikut-artis">
          {baris.artis}{baris.diminta_oleh ? ` · ${baris.diminta_oleh}` : ''}
        </div>
      </div>
    </div>
  )
}

/** Equalizer kecil untuk bilah judul. Berhenti saat dijeda. */
export function Equalizer({ jalan = true }: { jalan?: boolean }) {
  return (
    <span className={`tv-eq ${jalan ? '' : 'tv-eq-diam'}`} aria-hidden>
      <i /><i /><i /><i />
    </span>
  )
}

const WARNA_KONFETI = ['#f0d67a', '#d4af37', '#38bdf8', '#a78bfa', '#f472b6', '#34d399', '#fb923c', '#ffffff']

/** Confetti CSS murni (transform saja). */
export function Konfeti({ jumlah = 42 }: { jumlah?: number }) {
  return (
    <div className="tv-konfeti" aria-hidden>
      {Array.from({ length: jumlah }).map((_, i) => {
        const r = (n: number) => ((i * 9301 + n * 49297) % 233280) / 233280
        return (
          <i key={i} style={{
            left: `${r(1) * 100}%`,
            background: WARNA_KONFETI[i % WARNA_KONFETI.length],
            animationDuration: `${3.2 + r(2) * 2.6}s`,
            animationDelay: `${r(3) * 1.6}s`,
            width: 8 + Math.round(r(4) * 6), height: 12 + Math.round(r(5) * 8),
            '--tv-geser': `${Math.round((r(6) - 0.5) * 180)}px`,
            '--tv-putar': `${Math.round(360 + r(7) * 720)}deg`,
          } as CSSProperties} />
        )
      })}
    </div>
  )
}

/** Angka yang menghitung naik dari 0 (menulis textContent, bukan state). */
export function AngkaNaik({ nilai, durasi = 1700, className }: { nilai: number; durasi?: number; className?: string }) {
  const ref = useRef<HTMLSpanElement>(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const diam = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches
    if (diam) { el.textContent = String(nilai); return }
    let id = 0
    const mulai = performance.now()
    const langkah = (now: number) => {
      const p = Math.min(1, (now - mulai) / durasi)
      const e = 1 - Math.pow(1 - p, 3)
      el.textContent = String(Math.round(nilai * e))
      if (p < 1) id = requestAnimationFrame(langkah)
    }
    id = requestAnimationFrame(langkah)
    return () => cancelAnimationFrame(id)
  }, [nilai, durasi])
  return <span ref={ref} className={className}>0</span>
}
